import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';

// Redux
import { setOrderDataAction } from '../../redux/Actions/CreateOrderActions';
import { getBillingDetailsApi, addGiftCardApi } from '../../redux/Reducers/commonApis';

// Utils
import { amountCheckFn, textCheckFn } from '../../utils/commonUtil';
import { titleCaseFormatter } from '../../utils/stringFormatters';

// Components
import Loader from '../../components/Loader';
import Stripe from '../../components/Stripe';
import PromoCodeBanner from './PromoCodeBanner';

export default function StepPayment({ onContinue }) {
  const dispatch = useDispatch();
  const { orderPromoCode, orderServiceAddress } = useSelector((s) => s.createNewOrder);
  const [loading, setLoading] = useState(false);
  const [billing, setBilling] = useState({});
  const [payType, setPayType] = useState(null);
  const [payMethod, setPayMethod] = useState(null);
  const [addNewCard, setAddNewCard] = useState(false);
  const [promoCode, setPromoCode] = useState(orderPromoCode || '');
  const [giftBalance, setGiftBalance] = useState(null);
  const [giftCardNum, setGiftCardNum] = useState('');
  const [giftError, setGiftError] = useState(null);

  const fetchBilling = (id) => {
    setLoading(true);
    getBillingDetailsApi(id).then((res) => {
      setLoading(false);
      if (res?.status === 200 || res?.status === 201) {
        const det = res?.data?.addresses?.find((a) => a.id === id);
        dispatch(setOrderDataAction({ orderPaymentDetails: det || null }));
        setBilling(det || {});
        setPayType(det?.billing_details?.default_payment_method_type || null);
        setPayMethod(det?.billing_details?.default_payment_method || null);
        setGiftBalance(res?.data?.available_gift_card_balance || null);
        if (
          det?.billing_details?.default_payment_method_type === 'STRIPE' &&
          !det?.billing_details?.default_payment_method
        )
          setAddNewCard(true);
        else setAddNewCard(false);
      } else {
        toast.error(res?.message || 'Something went wrong');
      }
    });
  };

  useEffect(() => {
    orderServiceAddress?.id && fetchBilling(orderServiceAddress.id);
  }, [orderServiceAddress?.id]);
  useEffect(() => {
    if (promoCode === '') dispatch(setOrderDataAction({ orderPromoCode: promoCode }));
  }, [promoCode]);

  const addGiftCard = () => {
    if (!giftCardNum) {
      toast.error('Enter a gift card number');
      setGiftError('Enter a gift card number');
      return;
    }
    setLoading(true);
    addGiftCardApi({ gift_card_code: giftCardNum }).then((res) => {
      setGiftError(null);
      setLoading(false);
      if (res?.status === 200 || res?.status === 201) {
        toast.success(res?.message || 'Gift card added');
        setGiftCardNum('');
        fetchBilling(orderServiceAddress?.id);
      } else {
        toast.error(res?.message || 'Invalid gift card');
        setGiftError(res?.message || 'Invalid');
      }
    });
  };

  const valid = (() => {
    if (payType === 'ACH' || payType === 'CHECK') return true;
    if (payType === 'STRIPE' && payMethod) return true;
    return false;
  })();

  return (
    <div>
      {loading ? (
        <Loader text='Loading payment...' classes='h-24' />
      ) : (
        <div className='space-y-4'>
          {/* Payment method */}
          <div className='rounded-lg border border-slate-200 bg-white p-3'>
            <p className='text-xs font-semibold text-slate-500'>Payment Method</p>
            {payType === 'STRIPE' && payMethod && !addNewCard ? (
              <p className='mt-1 text-sm font-semibold text-slate-800'>
                {titleCaseFormatter(billing?.billing_details?.default_payment_method?.card_brand)} ••••{' '}
                {billing?.billing_details?.default_payment_method?.card_last4}
              </p>
            ) : payType === 'ACH' || payType === 'CHECK' ? (
              <p className='mt-1 text-sm font-semibold text-slate-800'>{textCheckFn(payType)}</p>
            ) : null}
            {addNewCard && (
              <div className='mt-2'>
                <Stripe
                  addressid={orderServiceAddress?.id}
                  cardAdded={() => {
                    fetchBilling(orderServiceAddress?.id);
                    setAddNewCard(false);
                  }}
                />
              </div>
            )}
            {!addNewCard && payType !== 'ACH' && payType !== 'CHECK' && (
              <button
                className='text-client-300 mt-2 text-xs font-semibold hover:underline'
                onClick={() => setAddNewCard(true)}
              >
                {payType === 'STRIPE' && payMethod ? 'Update card' : 'Add credit card'}
              </button>
            )}
          </div>
          {/* Gift card */}
          {giftBalance > 0 && (
            <div className='rounded-lg border border-slate-200 bg-white p-3'>
              <p className='text-xs font-semibold text-slate-500'>Gift Card Balance</p>
              <p className='mt-1 text-sm font-semibold text-green-600'>{amountCheckFn(giftBalance)}</p>
            </div>
          )}
          <div className='rounded-lg border border-slate-200 bg-white p-3'>
            <p className='mb-2 text-xs font-semibold text-slate-500'>Add a Gift Card</p>
            <div className='flex gap-2'>
              <input
                className='flex-1 rounded border border-slate-200 px-3 py-1.5 text-sm'
                placeholder='Gift card code'
                value={giftCardNum}
                onChange={(e) => setGiftCardNum(e.target.value)}
              />
              <button
                className='bg-client-300 rounded px-4 py-1.5 text-xs font-semibold text-white'
                onClick={addGiftCard}
              >
                Add
              </button>
            </div>
            {giftError && <p className='mt-1 text-xs text-red-500'>{giftError}</p>}
          </div>
          {/* Promo code */}
          <PromoCodeBanner
            onSelectCode={(code) => {
              setPromoCode(code);
              dispatch(setOrderDataAction({ orderPromoCode: code }));
            }}
          />
          <div className='rounded-lg border border-slate-200 bg-white p-3'>
            <p className='mb-2 text-xs font-semibold text-slate-500'>Promo Code</p>
            <div className='flex gap-2'>
              <input
                className='flex-1 rounded border border-slate-200 px-3 py-1.5 text-sm'
                placeholder='Enter code'
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
              />
              <button
                className='bg-client-300 rounded px-4 py-1.5 text-xs font-semibold text-white'
                onClick={() => dispatch(setOrderDataAction({ orderPromoCode: promoCode }))}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Continue button */}
      <div className='mt-4 flex justify-end'>
        <button
          type='button'
          className={`rounded-full px-6 py-2 text-sm font-semibold text-white transition ${
            !valid ? 'cursor-not-allowed bg-slate-300' : 'bg-client-300 hover:bg-client-400 shadow'
          }`}
          onClick={onContinue}
          disabled={!valid}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
